export const VOTE_REMINDER_OFFSETS_MINUTES = Object.freeze([10080, 4320, 1440, 120]);
export const UPCOMING_REMINDER_OFFSETS_MINUTES = Object.freeze([10080, 1440, 60]);
export const MIN_REMINDER_OFFSET_MINUTES = 10;
export const MAX_REMINDER_OFFSET_MINUTES = 30 * 24 * 60;
export const MAX_REMINDER_OFFSETS = 7;
export const MAX_PRESET_REMINDER_OFFSETS = 3;
export const MAX_CUSTOM_REMINDER_OFFSETS = 4;

const ROOT_KEYS = new Set([
  "enabled",
  "sessionVotes",
  "basicPollVotes",
  "upcomingSessions",
  "channels",
  "discord",
  "updatedAt",
  "updatedBy",
]);

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(value, required, optional = []) {
  if (!isPlainObject(value)) return false;
  const keys = Object.keys(value);
  const allowed = new Set([...required, ...optional]);
  return required.every((key) => keys.includes(key)) && keys.every((key) => allowed.has(key));
}

function isBoolean(value) {
  return value === true || value === false;
}

export function validateReminderOffsets(value, enabled) {
  if (!Array.isArray(value) || value.length > MAX_REMINDER_OFFSETS) return false;
  if (enabled && value.length === 0) return false;
  const unique = new Set(value);
  return (
    unique.size === value.length &&
    value.every(
      (offset) =>
        Number.isInteger(offset) &&
        offset >= MIN_REMINDER_OFFSET_MINUTES &&
        offset <= MAX_REMINDER_OFFSET_MINUTES
    )
  );
}

export function createDefaultReminderSettings({ discordLinked = false } = {}) {
  return {
    enabled: false,
    sessionVotes: { enabled: true, offsetsMinutes: [1440, 120] },
    basicPollVotes: {
      enabled: true,
      offsetsMinutes: [1440, 120],
      embeddedScope: "requiredOnly",
    },
    upcomingSessions: { enabled: false, offsetsMinutes: [1440, 60] },
    channels: { inApp: true, email: false, discord: discordLinked === true },
    discord: { voteMentionMode: "nonVoters", upcomingMentionMode: "none" },
  };
}

function invalidResult(reason, options) {
  return { valid: false, reason, settings: createDefaultReminderSettings(options) };
}

export function normalizeReminderSettings(value, options = {}) {
  if (value === null || value === undefined) {
    return { valid: true, reason: "missing", settings: createDefaultReminderSettings(options) };
  }
  if (!isPlainObject(value) || Object.keys(value).some((key) => !ROOT_KEYS.has(key))) {
    return invalidResult("unknown-or-invalid-root", options);
  }
  if (
    !hasExactKeys(value, [
      "enabled",
      "sessionVotes",
      "basicPollVotes",
      "upcomingSessions",
      "channels",
      "discord",
    ], ["updatedAt", "updatedBy"]) ||
    !isBoolean(value.enabled)
  ) {
    return invalidResult("invalid-root-shape", options);
  }

  const { sessionVotes, basicPollVotes, upcomingSessions, channels, discord } = value;
  if (
    !hasExactKeys(sessionVotes, ["enabled", "offsetsMinutes"]) ||
    !isBoolean(sessionVotes.enabled) ||
    !validateReminderOffsets(sessionVotes.offsetsMinutes, sessionVotes.enabled)
  ) return invalidResult("invalid-session-votes", options);
  if (
    !hasExactKeys(basicPollVotes, ["enabled", "offsetsMinutes", "embeddedScope"]) ||
    !isBoolean(basicPollVotes.enabled) ||
    basicPollVotes.embeddedScope !== "requiredOnly" ||
    !validateReminderOffsets(basicPollVotes.offsetsMinutes, basicPollVotes.enabled)
  ) return invalidResult("invalid-basic-poll-votes", options);
  if (
    !hasExactKeys(upcomingSessions, ["enabled", "offsetsMinutes"]) ||
    !isBoolean(upcomingSessions.enabled) ||
    !validateReminderOffsets(upcomingSessions.offsetsMinutes, upcomingSessions.enabled)
  ) return invalidResult("invalid-upcoming-sessions", options);
  if (
    !hasExactKeys(channels, ["inApp", "email", "discord"]) ||
    !isBoolean(channels.inApp) ||
    !isBoolean(channels.email) ||
    !isBoolean(channels.discord) ||
    (value.enabled && !channels.inApp && !channels.email && !channels.discord)
  ) return invalidResult("invalid-channels", options);
  if (
    !hasExactKeys(discord, ["voteMentionMode", "upcomingMentionMode"]) ||
    !["nonVoters", "none"].includes(discord.voteMentionMode) ||
    !["none", "notifyRole"].includes(discord.upcomingMentionMode)
  ) return invalidResult("invalid-discord-policy", options);
  if (value.updatedBy !== undefined && typeof value.updatedBy !== "string") {
    return invalidResult("invalid-updated-by", options);
  }

  return {
    valid: true,
    reason: null,
    settings: {
      enabled: value.enabled,
      sessionVotes: {
        enabled: sessionVotes.enabled,
        offsetsMinutes: [...sessionVotes.offsetsMinutes].sort((a, b) => b - a),
      },
      basicPollVotes: {
        enabled: basicPollVotes.enabled,
        offsetsMinutes: [...basicPollVotes.offsetsMinutes].sort((a, b) => b - a),
        embeddedScope: "requiredOnly",
      },
      upcomingSessions: {
        enabled: upcomingSessions.enabled,
        offsetsMinutes: [...upcomingSessions.offsetsMinutes].sort((a, b) => b - a),
      },
      channels: { inApp: channels.inApp, email: channels.email, discord: channels.discord },
      discord: {
        voteMentionMode: discord.voteMentionMode,
        upcomingMentionMode: discord.upcomingMentionMode,
      },
    },
  };
}

export function createReminderSettingsSnapshot(value, options = {}) {
  return normalizeReminderSettings(value, options).settings;
}

export function resolveEffectiveReminderSettings({
  resourceValue,
  parentValue,
  groupValue,
  options = {},
} = {}) {
  const candidates = [
    ["resource", resourceValue],
    ["parent", parentValue],
    ["group", groupValue],
  ];

  for (const [source, value] of candidates) {
    if (value === undefined) continue;
    const normalized = normalizeReminderSettings(value, options);
    if (!normalized.valid) {
      return {
        valid: false,
        reason: normalized.reason,
        source: `${source}-invalid`,
        settings: createDefaultReminderSettings(options),
      };
    }
    return {
      valid: true,
      reason: normalized.reason,
      source,
      settings: normalized.settings,
    };
  }

  return {
    valid: true,
    reason: "missing",
    source: "default",
    settings: createDefaultReminderSettings(options),
  };
}
